import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { Medal, Trophy, Award } from "lucide-react";
import Mandala from "./Mandala";
import { Eyebrow, Reveal, EASE } from "./Reveal";

function CountUp({ to, suffix = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.5 });
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, to, {
            duration: 1.8,
            ease: EASE,
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);
    return (
        <span ref={ref}>
            {val}
            {suffix}
        </span>
    );
}

const CARDS = [
    {
        id: "gold-medalist",
        icon: Medal,
        kicker: "National & International",
        body: "YOGA GOLD MEDALIST",
        counter: null,
    },
    {
        id: "world-record",
        icon: Trophy,
        kicker: "World Record Holder",
        body: null,
        counter: { to: 4, suffix: "X" },
    },
    {
        id: "competition-coach",
        icon: Award,
        kicker: "Competition-Level",
        body: "ADVANCED YOGASANA COACH",
        counter: null,
    },
];

export default function Achievements() {
    return (
        <section
            id="achievements"
            data-testid="achievements-section"
            className="relative py-20 sm:py-28 lg:py-32"
        >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,160,23,0.07),transparent_60%)]" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Eyebrow center>Proven Excellence</Eyebrow>
                <Reveal>
                    <h2 className="mt-6 text-center font-display text-5xl tracking-wide sm:text-6xl lg:text-7xl">
                        ACHIEVE<span className="text-gold-grad">MENTS</span>
                    </h2>
                    <div className="mx-auto mt-5 h-px w-28 bg-gradient-to-r from-transparent via-[#F5C542] to-transparent" />
                </Reveal>

                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {CARDS.map((card, i) => {
                        const Icon = card.icon;
                        return (
                            <motion.div
                                key={card.id}
                                data-testid={`achievement-card-${card.id}`}
                                className="group relative overflow-hidden rounded-2xl border border-[#D4A017]/20 bg-gradient-to-b from-white/[0.05] to-white/[0.01] p-8 transition-all duration-500 hover:-translate-y-2 hover:border-[#F5C542]/50 hover:shadow-[0_24px_60px_-20px_rgba(212,160,23,0.35)]"
                                initial={{ opacity: 0, y: 44 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.25 }}
                                transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
                            >
                                <div className="absolute -right-10 -top-10 h-44 w-44 opacity-0 transition-opacity duration-700 group-hover:opacity-40">
                                    <Mandala className="h-full w-full animate-spin-slower" petals={10} inner={6} />
                                </div>

                                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D4A017]/45 bg-[#D4A017]/[0.08] transition-all duration-500 group-hover:border-[#F5C542] group-hover:shadow-[0_0_26px_rgba(245,197,66,0.35)]">
                                    <Icon size={22} className="text-[#F5C542]" />
                                </span>

                                <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.3em] text-[#B8B8B8]">
                                    {card.kicker}
                                </p>

                                {card.counter ? (
                                    <p className="mt-3 font-display text-8xl leading-none text-gold-grad">
                                        <CountUp to={card.counter.to} suffix={card.counter.suffix} />
                                    </p>
                                ) : (
                                    <p className="mt-3 font-display text-4xl leading-[1.05] tracking-wide text-white">
                                        {card.body}
                                    </p>
                                )}

                                <div className="mt-6 h-px w-full bg-gradient-to-r from-[#D4A017]/40 to-transparent" />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
